import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Exercise } from "@/types";
import { Button } from "@/components/ui/button";
import { Youtube, ExternalLink, RefreshCw } from "lucide-react";
import { VideoFeedbackSection } from "./video-feedback-section";
import { trackEvent } from "@/lib/analytics/posthog";

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (
        element: HTMLElement | string,
        config: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: () => void;
            onError?: (event: { data: number }) => void;
            onStateChange?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  destroy(): void;
  getPlayerState(): number;
}

// Extract YouTube video ID from URL (handles regular videos and Shorts)
function extractYouTubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/^.*(youtu.be\/|v\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/);
  return match?.[2]?.length === 11 ? match[2] : null;
}

// YouTube Player component with error detection
function YouTubePlayer({
  videoId,
  onEmbedBlocked,
}: {
  videoId: string;
  onEmbedBlocked: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const [apiReady, setApiReady] = useState(false);

  // Load YouTube API
  useEffect(() => {
    if (window.YT) {
      setApiReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;

    window.onYouTubeIframeAPIReady = () => setApiReady(true);
    document.body.appendChild(script);

    return () => {
      window.onYouTubeIframeAPIReady = undefined;
    };
  }, []);

  // Create player when API is ready
  useEffect(() => {
    if (!apiReady || !containerRef.current || playerRef.current) return;

    playerRef.current = new window.YT.Player(containerRef.current, {
      videoId,
      playerVars: {
        autoplay: 0,
        controls: 1,
        rel: 0,
        fs: 1,
        modestbranding: 1,
      },
      events: {
        onError: (event) => {
          // Error codes 101 and 150 = embedding disabled by video owner
          if ([101, 150].includes(event.data)) {
            onEmbedBlocked();
          }
        },
      },
    });

    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [apiReady, videoId, onEmbedBlocked]);

  return (
    <div
      ref={containerRef}
      className="w-full aspect-video rounded-xl overflow-hidden bg-black/20"
    />
  );
}

// Error types matching server-side VideoSearchErrorType
type VideoSearchErrorType = "API_KEY_MISSING" | "API_ERROR" | "NO_RESULTS" | "RATE_LIMITED";

interface VideoState {
  status: "idle" | "loading" | "success" | "error" | "embed-blocked";
  videoUrl: string | null;
  videoId: string | null;
  videoTitle: string | null;
  error: string | null;
  errorType?: VideoSearchErrorType;
}

/**
 * Returns user-friendly error message based on error type
 */
function getErrorMessage(errorType?: VideoSearchErrorType, fallbackError?: string | null): string {
  switch (errorType) {
    case "API_KEY_MISSING":
      return "Video search is temporarily unavailable. Please try again later.";
    case "RATE_LIMITED":
      return "Too many searches. Please wait a moment and try again.";
    case "NO_RESULTS":
      return "No video found for this exercise. Try searching YouTube directly.";
    case "API_ERROR":
      return "Something went wrong while searching. Please try again.";
    default:
      return fallbackError || "Failed to search for video. Please try again.";
  }
}

interface ExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise?: any;
  exerciseName?: string; // Keep for backward compatibility
  trainingPlanId?: number; // For video feedback feature
  dayNumber?: number; // For video feedback feature
}

export function ExerciseModal({ isOpen, onClose, exercise, exerciseName, trainingPlanId, dayNumber }: ExerciseModalProps) {
  // Use direct exercise data from Tencent agent if available, otherwise fetch from API
  const shouldFetchFromAPI = !exercise && !!exerciseName;
  const { data: fetchedExercise, isLoading, error } = useQuery({
    queryKey: ["/api/exercises/name", exerciseName],
    queryFn: () => api.getExerciseByName(exerciseName!),
    enabled: isOpen && shouldFetchFromAPI && !!exerciseName,
  });

  // Video search state
  const [videoState, setVideoState] = useState<VideoState>({
    status: "idle",
    videoUrl: null,
    videoId: null,
    videoTitle: null,
    error: null,
    errorType: undefined,
  });

  // Use provided exercise data or fetched data
  const exerciseData = exercise || fetchedExercise;

  // Build YouTube search URL from exercise title (fallback for error states)
  const exerciseTitle = (exerciseData?.exercise || exerciseData?.name || "").toString().trim();
  const ytQuery = exerciseTitle ? exerciseTitle.split(/\s+/).join("+") : "";
  const ytSearchUrl = ytQuery ? `https://www.youtube.com/results?search_query=${ytQuery}` : null;

  // Handle video search
  const handleSearchVideo = useCallback(async () => {
    if (!exerciseTitle) return;

    setVideoState({ status: "loading", videoUrl: null, videoId: null, videoTitle: null, error: null, errorType: undefined });

    try {
      const result = await api.searchExerciseVideo(exerciseTitle);

      if (result.success && result.videoUrl) {
        const videoId = extractYouTubeVideoId(result.videoUrl);

        if (videoId) {
          // Normal success - show embedded player
          setVideoState({
            status: "success",
            videoUrl: result.videoUrl,
            videoId,
            videoTitle: result.videoTitle,
            error: null,
            errorType: undefined,
          });
          trackEvent('exercise_video_searched', {
            exercise_name: exerciseTitle,
            search_status: 'success',
            video_title: result.videoTitle,
          });
        } else {
          // Video ID extraction failed - show external link instead
          setVideoState({
            status: "embed-blocked",
            videoUrl: result.videoUrl,
            videoId: null,
            videoTitle: result.videoTitle,
            error: null,
            errorType: undefined,
          });
          trackEvent('exercise_video_searched', {
            exercise_name: exerciseTitle,
            search_status: 'embed_blocked',
            video_title: result.videoTitle,
          });
        }
      } else {
        setVideoState({
          status: "error",
          videoUrl: null,
          videoId: null,
          videoTitle: null,
          error: result.error || null,
          errorType: result.errorType as VideoSearchErrorType | undefined,
        });
        trackEvent('exercise_video_searched', {
          exercise_name: exerciseTitle,
          search_status: 'error',
          error_type: result.errorType,
        });
      }
    } catch (err) {
      setVideoState({
        status: "error",
        videoUrl: null,
        videoId: null,
        videoTitle: null,
        error: null,
        errorType: undefined,
      });
      try {
        trackEvent('exercise_video_searched', {
          exercise_name: exerciseTitle,
          search_status: 'error',
          error_type: 'network_error',
        });
      } catch {}
    }
  }, [exerciseTitle]);

  // Reset video state when modal closes or exercise changes
  const resetVideoState = useCallback(() => {
    setVideoState({ status: "idle", videoUrl: null, videoId: null, videoTitle: null, error: null, errorType: undefined });
  }, []);

  // Handle embed blocked from YouTube player
  const handleEmbedBlocked = useCallback(() => {
    setVideoState((prev) => ({
      ...prev,
      status: "embed-blocked",
    }));
  }, []);

  // Reset video state when exercise changes or modal closes
  useEffect(() => {
    resetVideoState();
  }, [exerciseTitle, isOpen]);

  if (!isOpen) return null;

  // Normalize "Muscle Used" field from different possible key variants and types
  const muscleUsedRaw =
    exerciseData?.Muscle_Used ??
    exerciseData?.muscle_used ??
    exerciseData?.muscleUsed ??
    exerciseData?.musclesUsed;
  const muscleUsed: string[] | null = Array.isArray(muscleUsedRaw)
    ? muscleUsedRaw
    : typeof muscleUsedRaw === "string"
    ? [muscleUsedRaw]
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto glass-card dark:glass-card-dark rounded-3xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[var(--apple-green)]">
            {exerciseData?.exercise || exerciseData?.name || 'Exercise Details'}
          </DialogTitle>
        </DialogHeader>
        
        {isLoading && (
          <div className="space-y-4">
            <div className="skeleton h-64 rounded-2xl"></div>
            <div className="skeleton h-24 rounded-2xl"></div>
            <div className="skeleton h-32 rounded-2xl"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400">
              Failed to load exercise details. Please try again.
            </p>
          </div>
        )}

        {exerciseData && (
          <div className="space-y-6">
            {/* Exercise Video/Animation Placeholder - removed and replaced with link section later */}
            {/* How to Perform / Instructions */}
            {(exerciseData.how_to_perform || exerciseData.instructions) && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-1">
                  {exerciseData.how_to_perform ? 'How to Perform' : 'Instructions'}
                </h4>
                <div className="mb-4">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        className="text-xs text-white hover:text-white/80"
                      >
                        Not Sure About Weight Progression?
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Weight Progression Help</AlertDialogTitle>
                        <AlertDialogDescription>
                          Start with ~60–70% of your target weight to warm up, adding weight each set until you reach the target. Only count sets once you’re at the target weight.
                          <br />
                          <br />
                          Example: Program = 4 × 40 kg. If it takes 5 warm-up sets to reach 40 kg, you still do 4 working sets at 40 kg. Warm-ups don’t count; keep perfect form.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogAction>Got it</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="space-y-3 text-gray-700 dark:text-gray-300">
                  {exerciseData.how_to_perform ? (
                    exerciseData.how_to_perform.map((step: string, index: number) => (
                      <p key={index}>{index + 1}. {step}</p>
                    ))
                  ) : (
                    exerciseData.instructions.map((instruction: string, index: number) => (
                      <p key={index}>{index + 1}. {instruction}</p>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Muscle Used */}
            {muscleUsed && muscleUsed.length > 0 && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-4">Muscle Used</h4>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  {muscleUsed.map((line, idx) => (
                    <p key={idx}>• {line}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Common Mistakes */}
            {(exerciseData.common_mistakes || exerciseData.formCues) && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-4">
                  {exerciseData.common_mistakes ? 'Common Mistakes' : 'Form Cues'}
                </h4>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  {exerciseData.common_mistakes ? (
                    exerciseData.common_mistakes.map((mistake: string, index: number) => (
                      <p key={index}>• {mistake}</p>
                    ))
                  ) : (
                    exerciseData.formCues.map((cue: string, index: number) => (
                      <p key={index}>• {cue}</p>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Exercise Demonstration */}
            {exerciseTitle && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6 border border-white/10 shadow-sm">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-2">Exercise Demonstration</h4>

                {/* Idle State - Show Button */}
                {videoState.status === "idle" && (
                  <Button
                    onClick={handleSearchVideo}
                    variant="apple"
                    className="mt-4 w-full rounded-xl flex items-center justify-center gap-2"
                  >
                    <Youtube className="w-5 h-5" />
                    Watch A Demonstration Video
                  </Button>
                )}

                {/* Loading State */}
                {videoState.status === "loading" && (
                  <div className="mt-4 space-y-3">
                    <div className="skeleton h-10 rounded-xl"></div>
                    <div className="flex items-center justify-center gap-2 text-gray-400">
                      <div className="w-2 h-2 bg-[var(--apple-green)] rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-[var(--apple-green)] rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                      <div className="w-2 h-2 bg-[var(--apple-green)] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                    </div>
                    <p className="text-center text-sm text-gray-500">Searching for video...</p>
                  </div>
                )}

                {/* Success State - Show Embedded Video */}
                {videoState.status === "success" && videoState.videoId && (
                  <div className="mt-4 space-y-3">
                    {videoState.videoTitle && (
                      <p className="text-sm font-medium text-white truncate">{videoState.videoTitle}</p>
                    )}

                    <YouTubePlayer
                      videoId={videoState.videoId}
                      onEmbedBlocked={handleEmbedBlocked}
                    />

                    <Button
                      onClick={resetVideoState}
                      variant="ghost"
                      size="sm"
                      className="w-full text-gray-400 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Search for a different video
                    </Button>
                  </div>
                )}

                {/* Embed Blocked State - Show Link Instead */}
                {videoState.status === "embed-blocked" && videoState.videoUrl && (
                  <div className="mt-4 space-y-3">
                    <div className="bg-yellow-900/20 rounded-xl p-4 text-center">
                      <p className="text-yellow-400 text-sm">This video cannot be embedded.</p>
                    </div>
                    <a
                      href={videoState.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-xl border border-white/10 p-4 hover:bg-[var(--dark-3)] transition"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/20">
                          <Youtube className="w-6 h-6 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {videoState.videoTitle || "Exercise Tutorial"}
                          </p>
                          <p className="text-sm text-gray-400 flex items-center gap-1">
                            Watch on YouTube <ExternalLink className="w-3 h-3" />
                          </p>
                        </div>
                      </div>
                    </a>
                    <Button
                      onClick={resetVideoState}
                      variant="ghost"
                      size="sm"
                      className="w-full text-gray-400 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Search for a different video
                    </Button>
                  </div>
                )}

                {/* Error State */}
                {videoState.status === "error" && (
                  <div className="mt-4 space-y-3">
                    <div className="bg-red-900/20 rounded-xl p-4 text-center">
                      <p className="text-red-400 text-sm">
                        {getErrorMessage(videoState.errorType, videoState.error)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSearchVideo}
                        variant="outline"
                        className="flex-1 rounded-xl"
                        disabled={videoState.errorType === "RATE_LIMITED"}
                      >
                        Try Again
                      </Button>
                      {ytSearchUrl && (
                        <Button
                          onClick={() => window.open(ytSearchUrl, "_blank")}
                          variant="ghost"
                          className="flex-1 rounded-xl"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Search YouTube
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Purpose */}
            {exerciseData.purpose && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-4">Purpose</h4>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  {Array.isArray(exerciseData.purpose) ? (
                    exerciseData.purpose.map((reason: string, index: number) => (
                      <p key={index}>• {reason}</p>
                    ))
                  ) : (
                    <p>{exerciseData.purpose}</p>
                  )}
                </div>
              </div>
            )}

            {/* Exercise Details */}
            {(exerciseData.sets || exerciseData.reps || exerciseData.duration || exerciseData.weight) && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-green)] mb-4">Exercise Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  {exerciseData.sets && (
                    <div className="text-center p-3 bg-gray-50 dark:bg-[var(--dark-3)] rounded-xl">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Sets</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">{exerciseData.sets}</p>
                    </div>
                  )}
                  {(exerciseData.reps || exerciseData.duration) && (
                    <div className="text-center p-3 bg-gray-50 dark:bg-[var(--dark-3)] rounded-xl">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{exerciseData.duration ? 'Duration' : 'Reps'}</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">{exerciseData.duration || exerciseData.reps}</p>
                    </div>
                  )}
                  {exerciseData.weight && (
                    <div className="text-center p-3 bg-gray-50 dark:bg-[var(--dark-3)] rounded-xl">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Weight</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">{exerciseData.weight}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alternatives */}
            {exerciseData.alternatives && Array.isArray(exerciseData.alternatives) && exerciseData.alternatives.length > 0 && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-[var(--apple-teal)] mb-4">Alternatives</h4>
                <div className="flex flex-wrap gap-2">
                  {exerciseData.alternatives.map((alternative: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 dark:bg-[var(--dark-3)] rounded-full text-sm"
                    >
                      {alternative}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Video Feedback Section */}
            {exerciseTitle && (
              <div className="bg-white dark:bg-[var(--dark-2)] rounded-2xl p-6">
                <VideoFeedbackSection
                  exerciseName={exerciseTitle}
                  trainingPlanId={trainingPlanId}
                  dayNumber={dayNumber}
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
